import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { itemsApi, type HouseholdItem } from '../api/items';
import { PageHeader } from '../components/ui/PageHeader';
import { FAB } from '../components/ui/FAB';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonList } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useToast } from '../components/ui/Toast';
import { normalizeText, formatCurrency } from '../utils/format';
import { EditIcon, ShoppingCartIcon, PlusIcon } from '../components/ui/icons';
import { useHousehold, useRenameHousehold } from '../hooks/useHousehold';
import { useAuthContext } from '../contexts/AuthContext';
import { Modal } from '../components/ui/Modal';
import { L } from '../lib/labels';

const CATEGORIES = [
  'Todos',
  'Alimentos',
  'Laticínios',
  'Limpeza',
  'Higiene',
  'Bebidas',
  'Hortifruti',
  'Padaria',
  'Carnes',
  'Congelados',
  'Outros',
];

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface ItemCardProps {
  item: HouseholdItem;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onPriceHistory: (id: string) => void;
  onToggleCart: (item: HouseholdItem) => void;
  batchMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}

function ItemCard({
  item,
  onEdit,
  onDelete,
  onPriceHistory,
  onToggleCart,
  batchMode,
  selected,
  onToggleSelect,
}: ItemCardProps) {
  const [showActions, setShowActions] = useState(false);

  const cartBadge = item.needsToBuy
    ? (item.quantityNeeded ?? item.defaultQuantityToBuy ?? '1')
    : null;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-colors ${
      selected ? 'border-brand-400 bg-brand-50' : 'border-gray-100'
    }`}>
      <div
        className="p-4 cursor-pointer active:bg-gray-50 transition-colors"
        onClick={() => {
          if (batchMode) {
            onToggleSelect(item.id);
          } else {
            setShowActions((v) => !v);
          }
        }}
      >
        <div className="flex items-start gap-3">
          {batchMode && (
            <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
              selected ? 'bg-brand-500 border-brand-500' : 'border-gray-300'
            }`}>
              {selected && (
                <svg width="12" height="12" fill="none" viewBox="0 0 12 12" stroke="white" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                </svg>
              )}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-base truncate">{item.name}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {item.quantityAtHome && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {L.items.atHomeLabel}: {item.quantityAtHome}
                </span>
              )}
              {item.needsToBuy && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  {L.items.buyLabel}: {cartBadge}
                </span>
              )}
            </div>
            {item.lastStoreName && item.lastUnitPrice && (
              <p className="text-xs text-gray-400 mt-1">
                {L.items.lastPrice}: {formatCurrency(item.lastUnitPrice)} em {item.lastStoreName}
              </p>
            )}
          </div>

          {!batchMode && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleCart(item); }}
              className={`flex-shrink-0 relative w-9 h-9 rounded-xl flex items-center justify-center transition-colors touch-manipulation ${
                item.needsToBuy
                  ? 'bg-amber-400 text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              aria-label={item.needsToBuy ? L.items.removeFromCart : L.items.addToCart}
            >
              <ShoppingCartIcon size={16} />
              {item.needsToBuy && cartBadge && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-amber-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {cartBadge.length > 4 ? cartBadge.slice(0, 4) : cartBadge}
                </span>
              )}
            </button>
          )}

          {!batchMode && (
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              className={`text-gray-400 flex-shrink-0 transition-transform ${showActions ? 'rotate-180' : ''}`}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {showActions && !batchMode && (
        <div className="flex border-t border-gray-100">
          <button
            onClick={() => onEdit(item.id)}
            className="flex-1 py-3 text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition-colors touch-manipulation"
          >
            {L.items.edit}
          </button>
          <div className="w-px bg-gray-100" />
          <button
            onClick={() => onPriceHistory(item.id)}
            className="flex-1 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors touch-manipulation"
          >
            {L.items.history}
          </button>
          <div className="w-px bg-gray-100" />
          <button
            onClick={() => onDelete(item.id)}
            className="flex-1 py-3 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors touch-manipulation"
          >
            {L.items.delete}
          </button>
        </div>
      )}
    </div>
  );
}

export function ItemsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const debouncedSearch = useDebounce(search);

  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchQty, setBatchQty] = useState('');

  const [cartModalItem, setCartModalItem] = useState<HouseholdItem | null>(null);
  const [cartModalQty, setCartModalQty] = useState('');

  const { data: items, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['items'],
    queryFn: () => itemsApi.list(),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const { user } = useAuthContext();
  const { data: household, isLoading: householdLoading } = useHousehold();
  const renameHousehold = useRenameHousehold();

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showFrozenModal, setShowFrozenModal] = useState(false);

  const isOwner = household?.ownerId === user?.userId;
  const isPremiumExpired =
    household?.plan !== 'PREMIUM' &&
    household?.premiumUntil != null &&
    new Date(household.premiumUntil) < new Date();

  const handleEditItem = (id: string) => {
    if (isPremiumExpired) { setShowFrozenModal(true); return; }
    navigate('/items/' + id + '/edit');
  };

  const handleOpenRename = () => {
    setNewListName(household?.name ?? '');
    setShowRenameModal(true);
  };

  const handleRename = async () => {
    if (!newListName.trim()) return;
    try {
      await renameHousehold.mutateAsync(newListName.trim());
      setShowRenameModal(false);
      addToast(L.errors.renameSuccess, 'success');
    } catch {
      addToast(L.errors.renameError, 'error');
    }
  };

  const deleteMutation = useMutation({
    mutationFn: itemsApi.delete,
    onSuccess: (_, id) => {
      queryClient.setQueryData<HouseholdItem[]>(['items'], (old) =>
        old ? old.filter((i) => i.id !== id) : []
      );
      addToast('Item excluído.', 'success');
      setDeleteTarget(null);
    },
    onError: () => addToast(L.errors.deleteItem, 'error'),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<HouseholdItem> }) =>
      itemsApi.update(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData<HouseholdItem[]>(['items'], (old) =>
        old ? old.map((i) => (i.id === updated.id ? updated : i)) : []
      );
      queryClient.invalidateQueries({ queryKey: ['items', { needsToBuy: true }] });
    },
    onError: () => addToast(L.errors.updateItem, 'error'),
  });

  const bulkMutation = useMutation({
    mutationFn: itemsApi.bulkMarkNeeded,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['items', { needsToBuy: true }] });
      setSelectedIds(new Set());
      setBatchMode(false);
      addToast(L.errors.itemsUpdate, 'success');
    },
    onError: () => addToast(L.errors.itemsUpdateError, 'error'),
  });

  const handleDelete = useCallback((id: string) => { setDeleteTarget(id); }, []);

  const handleToggleCart = useCallback((item: HouseholdItem) => {
    if (item.needsToBuy) {
      patchMutation.mutate({ id: item.id, data: { needsToBuy: false, quantityNeeded: null } });
    } else if (item.defaultQuantityToBuy) {
      patchMutation.mutate({
        id: item.id,
        data: { needsToBuy: true, quantityNeeded: item.defaultQuantityToBuy },
      });
    } else {
      setCartModalItem(item);
      setCartModalQty('');
    }
  }, [patchMutation]);

  const handleCartModalConfirm = () => {
    if (!cartModalItem) return;
    patchMutation.mutate({
      id: cartModalItem.id,
      data: { needsToBuy: true, quantityNeeded: cartModalQty.trim() || '1' },
    });
    setCartModalItem(null);
  };

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBatchMark = (needsToBuy: boolean) => {
    const payload = Array.from(selectedIds).map((id) => ({
      id,
      needsToBuy,
      quantityNeeded: needsToBuy ? (batchQty.trim() || undefined) : undefined,
    }));
    bulkMutation.mutate(payload);
  };

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      const matchSearch =
        !debouncedSearch || normalizeText(item.name).includes(normalizeText(debouncedSearch));
      const matchCategory =
        activeCategory === 'Todos' || item.category === activeCategory;
      return matchSearch && matchCategory;
    });
  }, [items, debouncedSearch, activeCategory]);

  const grouped = useMemo(() => {
    const groups = filtered.reduce(
      (acc, item) => {
        const key = item.category ?? 'Outros';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      },
      {} as Record<string, HouseholdItem[]>
    );
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // No household yet — show CTA
  if (!householdLoading && !household) {
    return (
      <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
        <PageHeader title={L.app.name} showLogo />
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 py-12">
          <div className="text-center">
            <div className="w-24 h-24 bg-brand-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{L.items.noListTitle}</h2>
            <p className="text-sm text-gray-500 mt-3 leading-relaxed max-w-xs mx-auto">
              {L.items.noListDesc}
            </p>
          </div>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/onboarding')}
          >
            {L.items.createList}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader
        title={household?.name ?? L.items.title}
        showLogo
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        action={
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setBatchMode((v) => !v); setSelectedIds(new Set()); }}
              className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors touch-manipulation ${
                batchMode ? 'bg-brand-100 text-brand-600' : 'text-gray-500 hover:bg-gray-100'
              }`}
              aria-label={batchMode ? L.items.exitBatch : L.items.selectMultiple}
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </button>
            {isOwner && (
              <button
                onClick={handleOpenRename}
                className="flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors touch-manipulation"
                aria-label={L.items.renameLabel}
              >
                <EditIcon size={18} />
              </button>
            )}
          </div>
        }
      />

      <div className="flex-1 px-4 py-4 space-y-3">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            width="18" height="18" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder={L.items.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={[
                'flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors touch-manipulation',
                activeCategory === cat
                  ? 'bg-brand-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-brand-300',
              ].join(' ')}
            >
              {cat}
            </button>
          ))}
        </div>

        {isLoading ? (
          <SkeletonList count={6} />
        ) : grouped.length === 0 ? (
          <EmptyState
            icon={
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#f3f4f6" />
                <path d="M20 24h24M20 32h24M20 40h16" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            }
            title={search ? L.items.notFound : L.items.addFirst}
            description={
              search
                ? L.items.notFoundDesc
                : L.items.addFirstDesc
            }
            action={!search ? { label: L.items.addButton, onClick: () => navigate('/items/new'), icon: <PlusIcon size={16} /> } : undefined}
          />
        ) : (
          <div className="space-y-4">
            {grouped.map(([category, catItems]) => (
              <div key={category}>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
                  {category}
                </p>
                <div className="space-y-2">
                  {catItems.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      onEdit={handleEditItem}
                      onDelete={handleDelete}
                      onPriceHistory={(id) => navigate(`/items/${id}/price-history`)}
                      onToggleCart={handleToggleCart}
                      batchMode={batchMode}
                      selected={selectedIds.has(item.id)}
                      onToggleSelect={handleToggleSelect}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {batchMode && (
        <div
          className="fixed left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 space-y-2"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 flex-1">
              {selectedIds.size} {selectedIds.size === 1 ? L.items.itemSelected : L.items.itemsSelected}
            </span>
            <input
              type="text"
              placeholder={L.items.batchQtyPlaceholder}
              value={batchQty}
              onChange={(e) => setBatchQty(e.target.value)}
              className="w-28 h-9 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleBatchMark(true)}
              disabled={selectedIds.size === 0 || bulkMutation.isPending}
              className="flex-1 h-10 bg-amber-400 text-white font-semibold rounded-xl disabled:opacity-40 text-sm touch-manipulation"
            >
              {L.items.markToBuy}
            </button>
            <button
              onClick={() => handleBatchMark(false)}
              disabled={selectedIds.size === 0 || bulkMutation.isPending}
              className="flex-1 h-10 bg-gray-200 text-gray-700 font-semibold rounded-xl disabled:opacity-40 text-sm touch-manipulation"
            >
              {L.items.unmark}
            </button>
          </div>
        </div>
      )}

      <FAB onClick={() => navigate('/items/new')} aria-label={L.items.addButton} />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Excluir item"
        message="Deseja excluir este item?"
        confirmLabel={L.items.delete}
        confirmVariant="danger"
        loading={deleteMutation.isPending}
      />

      <Modal
        isOpen={cartModalItem !== null}
        onClose={() => setCartModalItem(null)}
        title={L.items.qtyToBuy}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{cartModalItem?.name}</p>
          <input
            className="w-full h-11 px-4 rounded-xl border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={cartModalQty}
            onChange={(e) => setCartModalQty(e.target.value)}
            placeholder={L.items.qtyPlaceholder}
            autoFocus
          />
          <button
            onClick={handleCartModalConfirm}
            className="w-full h-12 bg-amber-400 text-white font-bold rounded-xl touch-manipulation"
          >
            {L.items.confirm}
          </button>
        </div>
      </Modal>

      <Modal isOpen={showFrozenModal} onClose={() => setShowFrozenModal(false)} title={L.items.frozenTitle}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{L.items.frozenDesc}</p>
          <button
            onClick={() => { setShowFrozenModal(false); navigate('/settings/payment'); }}
            className="w-full h-12 bg-brand-600 text-white font-bold rounded-xl touch-manipulation"
          >
            {L.items.seePremium}
          </button>
          <button onClick={() => setShowFrozenModal(false)} className="w-full text-sm text-gray-500 py-2 touch-manipulation">
            {L.items.close}
          </button>
        </div>
      </Modal>

      <Modal isOpen={showRenameModal} onClose={() => setShowRenameModal(false)} title={L.items.renameList}>
        <div className="space-y-4">
          <input
            className="w-full h-11 px-4 rounded-xl border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            placeholder={L.items.listNamePlaceholder}
            autoFocus
          />
          <button
            onClick={handleRename}
            disabled={renameHousehold.isPending || !newListName.trim()}
            className="w-full h-12 bg-brand-600 text-white font-bold rounded-xl disabled:opacity-50 touch-manipulation"
          >
            {renameHousehold.isPending ? L.items.saving : L.items.save}
          </button>
        </div>
      </Modal>
    </div>
  );
}
